import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/store";
import { BarChart3, TrendingUp, Package, ShoppingBag, Euro } from "lucide-react";

export default function AdminAnalytics() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user?.is_admin) return;
    api.get("/admin/analytics/summary").then(({ data }) => setData(data));
  }, [user]);

  if (loading || !user?.is_admin) return <div className="p-24 text-center text-muted-foreground">Cargando…</div>;
  if (!data) return <div className="p-24 text-center text-muted-foreground">Calculando métricas…</div>;

  const maxBrandRev = Math.max(1, ...data.top_brands.map((b) => b.revenue));
  const maxFamilyRev = Math.max(1, ...data.family_breakdown.map((f) => f.revenue));

  return (
    <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-12" data-testid="admin-analytics-page">
      <div className="mb-8">
        <div className="font-mono-caps text-royal">Backoffice</div>
        <h1 className="font-serif text-4xl md:text-5xl mt-2">Analytics</h1>
        <div className="mt-3 flex items-center gap-3 font-mono-caps text-xs">
          <Link to="/admin" className="text-muted-foreground hover:text-navy editorial-link" data-testid="admin-nav-products">Productos</Link>
          <Link to="/admin/pedidos" className="text-muted-foreground hover:text-navy editorial-link" data-testid="admin-nav-orders">Pedidos</Link>
          <span className="text-navy">Analytics</span>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="analytics-kpis">
        <Kpi label="Ingresos" value={`$${data.total_revenue.toFixed(2)}`} icon={Euro} highlight />
        <Kpi label="Pedidos" value={data.total_orders} icon={ShoppingBag} />
        <Kpi label="Unidades" value={data.total_units} icon={Package} />
        <Kpi label="AOV" value={`$${data.aov.toFixed(2)}`} icon={TrendingUp} />
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Top brands */}
        <section className="border border-border p-6" data-testid="top-brands">
          <h2 className="font-serif text-2xl mb-2">Top marcas</h2>
          <div className="font-mono-caps text-[10px] text-muted-foreground mb-6">Por ingresos de producto (sin envío)</div>
          {data.top_brands.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aún no hay ventas.</p>
          ) : (
            <ul className="space-y-3">
              {data.top_brands.map((b, i) => (
                <li key={b.brand} className="flex items-center gap-4">
                  <span className="font-mono-caps text-xs text-muted-foreground w-6">{String(i + 1).padStart(2, "0")}</span>
                  <div className="flex-1">
                    <div className="flex justify-between text-sm">
                      <span className="font-serif">{b.brand}</span>
                      <span>${b.revenue.toFixed(2)} · {b.units} uds</span>
                    </div>
                    <div className="mt-1 h-1 bg-muted overflow-hidden">
                      <div className="h-full bg-navy" style={{ width: `${(b.revenue / maxBrandRev) * 100}%` }} />
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Family breakdown */}
        <section className="border border-border p-6" data-testid="family-breakdown">
          <h2 className="font-serif text-2xl mb-2">Ventas por familia</h2>
          <div className="font-mono-caps text-[10px] text-muted-foreground mb-6">Distribución olfativa (sin envío)</div>
          {data.family_breakdown.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aún no hay ventas.</p>
          ) : (
            <ul className="space-y-3">
              {data.family_breakdown.map((f) => (
                <li key={f.family}>
                  <div className="flex justify-between text-sm">
                    <span className="capitalize font-serif">{f.family}</span>
                    <span>${f.revenue.toFixed(2)} · {f.units} uds</span>
                  </div>
                  <div className="mt-1 h-1 bg-muted overflow-hidden">
                    <div className="h-full bg-royal" style={{ width: `${(f.revenue / maxFamilyRev) * 100}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Top products */}
        <section className="md:col-span-2 border border-border p-6" data-testid="top-products">
          <h2 className="font-serif text-2xl mb-2">Top perfumes</h2>
          <div className="font-mono-caps text-[10px] text-muted-foreground mb-6">Los más vendidos hasta hoy</div>
          {data.top_products.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aún no hay ventas.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="font-mono-caps text-xs text-muted-foreground text-left">
                <tr>
                  <th className="py-2 w-8">#</th>
                  <th className="py-2">Perfume</th>
                  <th className="py-2">Marca</th>
                  <th className="py-2 text-right">Unidades</th>
                  <th className="py-2 text-right">Ingresos</th>
                </tr>
              </thead>
              <tbody>
                {data.top_products.map((p, i) => (
                  <tr key={p.product_id} className="border-t border-border">
                    <td className="py-2 font-mono-caps text-xs">{String(i + 1).padStart(2, "0")}</td>
                    <td className="py-2 font-serif">{p.name}</td>
                    <td className="py-2 font-mono-caps text-xs">{p.brand}</td>
                    <td className="py-2 text-right">{p.units}</td>
                    <td className="py-2 text-right">${p.revenue.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* Revenue by month */}
        <section className="md:col-span-2 border border-border p-6" data-testid="revenue-by-month">
          <h2 className="font-serif text-2xl mb-2">Ingresos por mes</h2>
          <div className="font-mono-caps text-[10px] text-muted-foreground mb-6">Últimos 12 meses</div>
          {data.revenue_by_month.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aún no hay ventas.</p>
          ) : (
            <div className="flex items-end gap-3 h-40">
              {(() => {
                const mx = Math.max(1, ...data.revenue_by_month.map((r) => r.revenue));
                return data.revenue_by_month.map((r) => (
                  <div key={r.month} className="flex-1 flex flex-col items-center gap-2 min-w-0">
                    <div className="w-full bg-muted flex items-end" style={{ height: "80%" }}>
                      <div className="w-full bg-navy" style={{ height: `${(r.revenue / mx) * 100}%` }} title={`$${r.revenue.toFixed(2)}`} />
                    </div>
                    <div className="font-mono-caps text-[10px] text-muted-foreground">{r.month}</div>
                    <div className="text-xs">${r.revenue.toFixed(0)}</div>
                  </div>
                ));
              })()}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, highlight }) {
  return (
    <div className={`border border-border p-6 ${highlight ? "bg-navy text-cream border-navy" : ""}`}>
      <div className="flex items-center gap-2 font-mono-caps text-[10px] opacity-80">
        <Icon strokeWidth={1.25} className="h-3 w-3" /> {label}
      </div>
      <div className="font-serif text-3xl mt-3">{value}</div>
    </div>
  );
}
